import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@/apollo';
import { 
  Zap, 
  CheckCircle, 
  AlertTriangle, 
  X, 
  RefreshCw,
  Clock,
  BarChart3
} from 'lucide-react';
import { OPTIMIZE_DATABASE } from '../../graphql/system-health';

interface DatabaseOptimizeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type OptimizationStage = 'confirm' | 'optimizing' | 'completed' | 'failed';

export const DatabaseOptimizeModal: React.FC<DatabaseOptimizeModalProps> = ({
  isOpen,
  onClose
}) => {
  const [stage, setStage] = useState<OptimizationStage>('confirm');
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [optimizationResults, setOptimizationResults] = useState<any>(null);

  const [optimizeDatabase] = useMutation(OPTIMIZE_DATABASE);

  const handleStartOptimization = async () => {
    try {
      setStage('optimizing');
      setProgress(0);
      setError(null);
      setOptimizationResults(null);

      // Show initial progress
      setCurrentStep('Starting database optimization...');
      setProgress(10);

      // Call the actual optimization mutation
      const result = await optimizeDatabase();
      
      if (result.data?.optimizeDatabase?.success) {
        setCurrentStep('Optimization completed successfully');
        setProgress(100);
        setOptimizationResults(result.data.optimizeDatabase);
        setStage('completed');
      } else {
        throw new Error(result.data?.optimizeDatabase?.message || 'Optimization failed');
      }
    } catch (err) {
      console.error('Database optimization error:', err);
      setError(err instanceof Error ? err.message : 'Failed to optimize database');
      setStage('failed');
    }
  };

  const handleClose = () => {
    setStage('confirm');
    setProgress(0);
    setCurrentStep('');
    setError(null);
    setOptimizationResults(null);
    onClose();
  };

  const getStageContent = () => {
    switch (stage) {
      case 'confirm':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-warning-bg)' }}>
              <Zap className="w-8 h-8" style={{ color: 'var(--color-warning)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Optimize Database
            </h3>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              This will analyze and optimize your database performance. The process may take several minutes and should be done during low-traffic periods.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6" style={{ backgroundColor: 'var(--color-warning-bg)', borderColor: 'var(--color-warning)' }}>
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 mr-2 mt-0.5" style={{ color: 'var(--color-warning)' }} />
                <div>
                  <h4 className="font-medium mb-1" style={{ color: 'var(--color-warning)' }}>Important</h4>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Database optimization may temporarily impact performance. It's recommended to run this during maintenance windows.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 border rounded-lg transition-colors"
                style={{ 
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleStartOptimization}
                className="px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2"
                style={{ backgroundColor: 'var(--color-warning)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-warning-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-warning)';
                }}
              >
                <Zap className="w-4 h-4" />
                <span>Start Optimization</span>
              </button>
            </div>
          </div>
        );

      case 'optimizing':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center animate-spin" style={{ backgroundColor: 'var(--color-accent-bg)' }}>
              <RefreshCw className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Optimizing Database
            </h3>
            <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              {currentStep}
            </p>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
              <div 
                className="h-2 rounded-full transition-all duration-500"
                style={{ 
                  backgroundColor: 'var(--color-accent)',
                  width: `${progress}%`
                }}
              />
            </div>
            
            <div className="flex items-center justify-center space-x-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
              <Clock className="w-4 h-4" />
              <span>Please wait while we optimize your database...</span>
            </div>
          </div>
        );

      case 'completed':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-success-bg)' }}>
              <CheckCircle className="w-8 h-8" style={{ color: 'var(--color-success)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Optimization Complete
            </h3>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              Your database has been successfully optimized. Performance improvements should be noticeable immediately.
            </p>
            
            {optimizationResults && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6" style={{ backgroundColor: 'var(--color-success-bg)', borderColor: 'var(--color-success)' }}>
                <div className="flex items-center mb-2">
                  <BarChart3 className="w-5 h-5 mr-2" style={{ color: 'var(--color-success)' }} />
                  <h4 className="font-medium" style={{ color: 'var(--color-success)' }}>Optimization Results</h4>
                </div>
                <div className="text-sm space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                  <div>• Status: {optimizationResults.success ? 'Success' : 'Failed'}</div>
                  <div>• Message: {optimizationResults.message}</div>
                  {optimizationResults.details && (
                    <div>• Details: {optimizationResults.details}</div>
                  )}
                </div>
              </div>
            )}
            
            <button
              onClick={handleClose}
              className="px-4 py-2 text-white rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--color-success)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-success-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-success)';
              }}
            >
              Close
            </button>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-error-bg)' }}>
              <AlertTriangle className="w-8 h-8" style={{ color: 'var(--color-error)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Optimization Failed
            </h3>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              {error || 'An unexpected error occurred during database optimization.'}
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 border rounded-lg transition-colors"
                style={{ 
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Close
              </button>
              <button
                onClick={handleStartOptimization}
                className="px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2"
                style={{ backgroundColor: 'var(--color-accent)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                }}
              >
                <RefreshCw className="w-4 h-4" />
                <span>Try Again</span>
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="rounded-xl shadow-xl max-w-md w-full mx-4"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-xl)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Database Optimization
          </h3>
          {stage === 'completed' || stage === 'failed' ? (
            <button
              onClick={handleClose}
              className="p-1 rounded-lg transition-colors"
              style={{ color: 'var(--color-text-muted)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <X className="w-5 h-5" />
            </button>
          ) : null}
        </div>

        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              {getStageContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};
